import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import PreviewFilePDFPage from './PreviewFilePDFPage';
import PreviewFileNav from './PreviewFileNav';
import classes from './PreviewFile.module.css';
import clsx from 'clsx';
import Loading from 'Components/Atoms/Loading/Loading';
import { useTranslation } from 'react-i18next';
import usePDFLoader from 'Hooks/usePDFLoader';

const createPages = (length) => Array.from({ length }, (_, i) => i + 1);

const gapBetweenPages = 24;
const padding = 16;

export default function PreviewFilePDF({ url, className, onPrint }) {
    const { pdf, hasError } = usePDFLoader(url);
    const [pageNum, setPageNum] = useState(1);
    const [zoom, setZoom] = useState(1);
    const [pagesDimensions, setPagesDimensions] = useState({});
    const containerRef = useRef(null);
    const [isScrollEventEnabled, setScrollEventEnabled] = useState(true);

    const { t } = useTranslation('common');

    const handleChangeZoom = (newZoom) => {
        const origin = {
            x: containerRef.current.offsetWidth / 2,
            y: containerRef.current.offsetHeight / 2,
        };

        handleZoom(newZoom, origin);
    };

    const handleZoom = (newZoom, origin) => {
        setScrollEventEnabled(false);

        const scrollLeft = containerRef.current.scrollLeft;
        const scrollTop = containerRef.current.scrollTop;

        setZoom(newZoom);

        const x = (origin.x + scrollLeft) / zoom;
        const y = (origin.y + scrollTop) / zoom;

        const newScrollLeft = x * newZoom - origin.x;
        const newScrollTop = y * newZoom - origin.y;

        containerRef.current.scrollTo({
            top: newScrollTop,
            left: newScrollLeft,
        });

        setTimeout(() => {
            setScrollEventEnabled(true);
        });
    };

    useEffect(() => {
        if (!containerRef.current) return;

        const handleScroll = (ev) => {
            if (!isScrollEventEnabled) {
                return;
            }

            const containerScrollTop = ev.target.scrollTop + padding;

            let currentPage = 1;
            let currentOffset = 0;

            for (const page in pagesDimensions) {
                if (currentOffset > containerScrollTop) {
                    break;
                }

                const { height } = pagesDimensions[page];
                const pageHeight = height * zoom;

                currentOffset += pageHeight + gapBetweenPages;

                currentPage = Number(page);
            }

            setPageNum(currentPage);
        };

        const container = containerRef.current;
        container.addEventListener('scroll', handleScroll);

        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [isScrollEventEnabled, pagesDimensions, zoom]);

    useEffect(() => {
        if (pdf) {
            setPageNum(1);
        }
    }, [pdf]);

    if (hasError) {
        return (
            <div className={clsx(classes.loading, className)}>
                <p className="opacity-50">{t('error-visualizing-file')}</p>
            </div>
        );
    }

    if (!pdf) {
        return (
            <div className={clsx(classes.loading, className)}>
                <Loading />
            </div>
        );
    }

    const pages = createPages(pdf.numPages);

    const scrollToPage = (pageNumber) => {
        setScrollEventEnabled(false);
        let targetOffset = 0;
        for (let i = 1; i < pageNumber; i++) {
            const pageHeight = pagesDimensions[i].height * zoom;
            targetOffset += pageHeight + gapBetweenPages;
        }

        containerRef.current.scrollTo({ top: targetOffset });

        setTimeout(() => {
            setScrollEventEnabled(true);
        });
        setPageNum(pageNumber);
    };

    return (
        <div className={clsx(classes.layout, className)}>
            <div className={classes.content} ref={containerRef}>
                <div className={classes.pages}>
                    {pages.map((i) => (
                        <PreviewFilePDFPage
                            key={i}
                            pdf={pdf}
                            pageNum={i}
                            zoom={zoom}
                            onRender={(dimensions) => {
                                setPagesDimensions((current) => {
                                    return {
                                        ...current,
                                        [i]: dimensions,
                                    };
                                });
                            }}
                        />
                    ))}
                </div>
            </div>

            <PreviewFileNav
                numPages={pdf.numPages}
                page={pageNum}
                onChangePage={scrollToPage}
                zoom={zoom}
                onChangeZoom={handleChangeZoom}
                onPrint={onPrint}
            />
        </div>
    );
}

PreviewFilePDF.propTypes = {
    url: PropTypes.string.isRequired,
    className: PropTypes.string,
    onPrint: PropTypes.func,
};
